import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import { alpha } from "@material-ui/core/styles";
import Add from "@material-ui/icons/Add";
import Edit from "@material-ui/icons/Edit";
import Avatar from "@material-ui/core/Avatar";
import Tooltip from "@material-ui/core/Tooltip";
import { useRef, useState, useEffect } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';

import { getAll } from "src/api/vendorServices";
import { Main } from "src/components/commons/Main";
import { VendorServiceForm } from "src/components/vendorServices/Form";
import Table from "src/components/commons/Table/Table";
import { formatPhone, renderSafeValue } from 'src/helpers';
import StatusChip from "src/components/commons/StatusChip";
import { DashboardEmptyState } from "src/components/icons/all/dashboard/DashboardEmptyState";
import { getVendorMatches } from 'src/api/vendorServices';
import RightDrawer from 'src/components/commons/RightDrawer';
import { InlineWidget } from 'react-calendly';
import { getFounderDetailsForVendorMatch } from 'src/api/vendorServices';
import DashboardDetails from 'src/components/campaigns/DashboardDetails';
import { getMatchActionButtons } from 'src/helpers/matchActions.jsx';

const statusMap = { IN_PROGRESS: "In Progress", ACTIVE: "Active", INACTIVE: "Inactive" };

const useStyles = makeStyles((theme) => ({
	avatar: {
		width: 40,
		height: 40,
		background: theme.palette.background.default,
		"& img": {
			objectFit: "contain",
		},
	},
	modal: {
		transform: "translate(-50%, -50%)",
	},
}));

export function VendorMatches() {
	const { state } = useLocation();
	const navigate = useNavigate();
	// const paginatedMatches = useLoaderData();
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const classes = useStyles();
	const [openFormModal, setOpenFormModal] = useState(state?.create || false);
	const selectedMatch = useRef(null);
	const { authUser, currentRole } = useSelector(({ auth }) => auth);
	const [matches, setMatches] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [error, setError] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [calendlyUrl, setCalendlyUrl] = useState('');
	const [viewOpen, setViewOpen] = useState(false);
	const [viewDetails, setViewDetails] = useState(null);

	useEffect(() => {
		async function fetchMatches() {
			// console.log('Fetching matches for user:', authUser, currentRole);
			try {
				setLoading(true);
				setError(false);
				const response = await getVendorMatches(authUser.id, { page: page + 1, limit: rowsPerPage });
				// console.log('API Response:', response);
				if (response && Array.isArray(response)) {
					setMatches(response || []);
					// Get total count from the first item if available
					const totalCount = response.length > 0 ? response[0].total_count : 0;
					// console.log('Total Count:', totalCount);
					setTotalCount(totalCount);
				} else {
					setMatches([]);
					setTotalCount(0);
				}
			} catch (e) {
				console.error("Error fetching vendor matches:", e);
				setError(true);
			} finally {
				setLoading(false);
			}
		}
		if (authUser?.id) fetchMatches();
	}, [authUser?.id, page, rowsPerPage]);

	const handleViewFounder = async (row) => {
		try {
			const details = await getFounderDetailsForVendorMatch(row.id);
			setViewDetails(details);
			setViewOpen(true);
		} catch (e) {
			console.error("Error fetching founder details:", e);
			setViewDetails(null);
			setViewOpen(false);
		}
	};

	const matchColumns = [
		{
			header: 'Offered Service',
			accessor: 'offered_service',
			align: 'left',
			render: (_, row) => renderSafeValue(row.offered_service, 'offered_service'),
		},
		{
			header: 'Match Status',
			accessor: 'status',
			align: 'center',
			render: (_, row) => <StatusChip status={row.status} />,
		},
		{
			header: 'Call Status',
			accessor: 'call_status',
			align: 'center',
			render: (_, row) => <StatusChip status={row.call_status} />,
		},
		{
			header: 'Founder',
			accessor: 'founder_name',
			align: 'center',
			// render: (_, row) => {
			// 	const isActive = row.status?.toLowerCase() === 'active';
			// 	return isActive ? row.founder_name : '*****';
			// },
		},
		{
			header: 'Founder Email',
			accessor: 'founder_email',
			align: 'center',
			// render: (_, row) => {
			// 	const isActive = row.status?.toLowerCase() === 'active';
			// 	return isActive ? row.founder_email : '*****';
			// },
		},
		{
			header: 'Founder Business Phone',
			accessor: 'founder_business_phone',
			align: 'center',
			// render: (_, row) => {
			// 	const isActive = row.status?.toLowerCase() === 'active';
			// 	return isActive ? formatPhone(row.founder_business_phone) : '*****';
			// },
		},
		{
			header: 'Founder Personal Phone',
			accessor: 'founder_personal_phone',
			align: 'center',
			// render: (_, row) => {
			// 	const isActive = row.status?.toLowerCase() === 'active';
			// 	return isActive ? formatPhone(row.founder_personal_phone) : '*****';
			// },
		},
		{
			header: 'Action',
			accessor: 'action',
			align: 'right',
			render: (_, row) => {
				return getMatchActionButtons({
					row,
					onView: handleViewFounder,
					viewButtonLabel: 'View Details',
					isViewDisabled: row.status?.toLowerCase() !== 'active',
					disableAccept: false,
					disableDecline: false,
				});
			},
		},
	];

	// Custom status renderer with background colors
	const renderStatus = (value) => {
		return <StatusChip status={value} />;
	};

	const handlePageChange = (event, newPage) => {
		setPage(newPage);
	};

	const handleRowsPerPageChange = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleScheduleMeeting = (row) => {
		// Replace with dynamic URL if needed
		setCalendlyUrl('https://calendly.com/catotechnology/30min');
		setDrawerOpen(true);
	};

	return (
		<Main>
			<Box display={"flex"} justifyContent="space-between" p={4} px={4} borderBottom={"1px solid rgb(239 232 251)"}>
				<Typography component={"h2"} variant="h6">
					Matches
				</Typography>
			</Box>
			{matches.length === 0 ? (
				<DashboardEmptyState 
					title="No Matches Found"
					description="Matches will appear here once you're matched with opportunities"
				/>
			) : (
				<Box p={4}>
						<Table
							columns={matchColumns}
							data={matches}
							loading={loading}
							error={error}
							page={page}
							rowsPerPage={rowsPerPage}
							totalCount={totalCount}
							onPageChange={handlePageChange}
							onRowsPerPageChange={handleRowsPerPageChange}
							stickyHeader
							className="border-0"
							emptyStateMessage="There are no pending matches at the moment"
						/>
						<RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
							{calendlyUrl && (
								<InlineWidget url={calendlyUrl} styles={{ height: '100vh' }} />
							)}
						</RightDrawer>
				</Box>
			)}
			<Dialog
				fullWidth={true}
				maxWidth="xs"
				open={openFormModal}
				onClose={() => setOpenFormModal(false)}
				arialabelledby={"form-vs-dialog"}
			>
				<DialogTitle id="form-vs-dialog" pb={0}>
					Match Details
				</DialogTitle>
				<VendorServiceForm vendorService={selectedMatch.current} close={() => setOpenFormModal(false)} />
			</Dialog>
            <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle>Founder Details</DialogTitle>
				<DialogContent>
					{viewDetails && <DashboardDetails details={viewDetails} userType={currentRole} />}
				</DialogContent>
			</Dialog>
		</Main>
	);
}

export function vendorMatchesLoader() {
	// Temporarily return empty data to test empty state
	return {
		data: [],
		total: 0,
		page: 1,
		limit: 10
	};
} 